import React, { useEffect, useRef, useState, useCallback } from "react";

import "./PrivacyPolicy.scss";
import PrivacyIcon from "../../../assets/images/png/privacy_icon.png";
import { Image } from "../../../components/__common/Image";
import { Typography } from "../../../components/__common/Typography/Typography";
import { Container } from "../../../components/Grid/Grid";
import { LinkButton, TTSButton } from "../../../components/__common/_controls";
import { Svg, SVG_ICONS } from "../../../components/__common/Svg/Svg";
import { VersionHistoryModal } from "../../../components/VersionHistoryModal/VersionHistoryModal";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import { LanguageModal } from "../../../components/LanguageModal/LanguageModal";
import { useTranslation } from "react-i18next";
import {
  EVENT_CATEGORY_PRIVACY_POLICY,
  EVENT_ACTION_PP_DOWNLOAD_PRIVACY_POLICY,
  EVENT_ACTION_PP_CHANGE_VERSION,
} from "../../../constants/analytics";
import FocusTrapping from "../../../components/__hoc/FocusTrapping";
import GoogleAnalytics from "../../../utils/GoogleAnalytics";
import { PrivacyState } from "../../../store/reducers/privacy-reducer";
import {
  GPPJsonConfig,
  VersionHistoryState,
} from "../../../types/version-history";
import { API_BASE_URL } from "../../../constants/api";
import Http from "../../../services/http";
import { UtilsLocale } from "../../../utils/UtilsLocale";

export const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  const [ppSource, setPpSource] = useState(
    `${API_BASE_URL}/privacy_policy/en.html`
  );
  const [isLanguageOpen, setLanguageOpen] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [hasVersionHistory, setHasVersionHistory] = useState(false);
  const [versionHistories, setVersionHistories] = useState<
    VersionHistoryState[]
  >([]);
  const [isHistoryOpen, setHistoryOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState<VersionHistoryState>();
  const layoutState = useSelector<ApplicationState, LayoutState>(
    (state) => state.layout
  );
  const privacyState = useSelector<ApplicationState, PrivacyState>(
    (state) => state.privacy
  );
  const { isDarkMode } = layoutState;
  const topRef = useRef<HTMLDivElement>(null);
  const { language } = privacyState;
  const [region, langCode] = language.split("-");

  const darkModeStyles = `
  html[data-theme="dark"] body {
    color: #a9a9a9 !important;
  }

  html[data-theme="dark"] .auto-style1 {
    color: #e5e5e5 !important;
  }

  html[data-theme="dark"] strong {
    color: #e5e5e5 !important;
  }

  html[data-theme="dark"] a {
    color: #0381fe !important;
  }

  html[data-theme="dark"] a strong {
    color: #0381fe !important;
  }
`;

  // set privacy policy source file based on selected version history or current version in language modal
  const setPPsourceFile = async (date: string, language: string) => {
    const [region, langCode] = language.split("-");
    const ppExist = UtilsLocale.hasPrivacyPolicy(language);
    const dateSource = date;
    const ppSourcePath = ppExist
      ? `${API_BASE_URL}/privacy_policy/${langCode}/${region}/${dateSource}.html`
      : `${API_BASE_URL}/privacy_policy/en.html`;

    setPpSource(ppSourcePath);
  };

  // get version history config based on selected language in language modal
  const loadPrivacyPolicyVersionHistory = useCallback(
    async (langCode: string, region: string) => {
      try {
        const url = `${API_BASE_URL}/privacy_policy/${langCode}/${region}/version_history.json`;
        const response: GPPJsonConfig = await Http.get(url);
        // Filter out draft versions from the version history list
        const publishedVersions = response.versions
          .filter((version) => version.status !== "DRAFT")
          .sort(
            (a, b) =>
              new Date(b.displayDate).getTime() -
              new Date(a.displayDate).getTime()
          );
        // Checker for showing version history button
        const shouldShowVersionHistory =
          response.enabled && publishedVersions.length > 1;
        setHasVersionHistory(shouldShowVersionHistory);

        if (shouldShowVersionHistory) {
          setVersionHistories(publishedVersions);
          // set default selected history
          const defaultHistory =
            publishedVersions.find(
              (version) => version.name === response.current
            ) || publishedVersions[0];

          setSelectedHistory(defaultHistory);
        } else {
          setVersionHistories([]);
          setSelectedHistory(undefined);
        }

        // Determine source file with fallback to global en.html
        if (response.current) {
          await setPPsourceFile(response.current, language);
        } else {
          const globalGPPURL = `${API_BASE_URL}/privacy_policy/ZZ/en/version_history.json`;
          const gppVersionHistoryResponse: GPPJsonConfig =
            await Http.get(globalGPPURL);
          setPpSource(
            `${API_BASE_URL}/privacy_policy/ZZ/en/${gppVersionHistoryResponse.current}.html`
          );
        }
      } catch (error: any) {
        console.error(`Failed to load privacy policy config`, error);
        setHasVersionHistory(false);
        setVersionHistories([]);
        setSelectedHistory(undefined);
        setPpSource(`${API_BASE_URL}/privacy_policy/en.html`);
      }
    },
    [language]
  );

  const handleSelectHistory = useCallback(
    async (history: VersionHistoryState) => {
      if (hasVersionHistory) {
        setSelectedHistory(history);
        await setPPsourceFile(history.name, language);
        setHistoryOpen(false);
        GoogleAnalytics.triggerEvent(
          EVENT_CATEGORY_PRIVACY_POLICY,
          EVENT_ACTION_PP_CHANGE_VERSION
        );
      }
    },
    [hasVersionHistory, language]
  );

  const iframeLoaded = () => {
    const iFrameID = document.getElementById("privacy-id") as HTMLIFrameElement;
    if (!iFrameID || !iFrameID.contentWindow || !iFrameID.contentDocument)
      return;

    const doc = iFrameID.contentDocument;
    const html = doc.getElementsByTagName("html")[0];

    // Apply theme attribute
    if (isDarkMode) {
      html.setAttribute("data-theme", "dark");
    } else {
      html.removeAttribute("data-theme");
    }

    // Inject CSS for dark mode
    const styleTagId = "dark-mode-injected-style";
    if (!doc.getElementById(styleTagId)) {
      const style = doc.createElement("style");
      style.id = styleTagId;
      style.innerHTML = darkModeStyles;
      doc.head.appendChild(style);
    }

    iFrameID.height = doc.body.scrollHeight + 50 + "px";
  };

  const handleCloseVersionHistory = () => {
    setHistoryOpen(false);
    document.getElementById("version-history-button")?.focus();
  };

  const handleClosePolicyLanguage = () => {
    setLanguageOpen(false);
    document.getElementById("policy-language-button")?.focus();
  };

  useEffect(() => {
    // Due to delayed layout calculation upon refresh, IFRAME is not loaded, adding a setTimeout will trigger a reflow after component mounts.
    const iframeReflow = setTimeout(() => {
      setIsIframeLoaded(true);
    }, 0);
    topRef.current?.focus();
    return () => {
      clearTimeout(iframeReflow);
    };
  }, []);

  useEffect(() => {
    if (language === "") return;
    loadPrivacyPolicyVersionHistory(langCode, region);
  }, [language]);

  useEffect(() => {
    iframeLoaded();
    window.addEventListener("resize", iframeLoaded);

    return () => {
      window.removeEventListener("resize", iframeLoaded);
    };
  }, [isDarkMode]);

  useEffect(() => {
    if (isLanguageOpen) {
      document.getElementById("language-modal-tts")?.focus();
    }
  }, [isLanguageOpen]);

  useEffect(() => {
    if (isHistoryOpen) {
      document.getElementById("version-modal-tts")?.focus();
    }
  }, [isHistoryOpen]);

  return (
    <div className="privacy-policy">
      <FocusTrapping
        onHandleClose={handleCloseVersionHistory}
        isActive={isHistoryOpen}
        modalId="version-history-modal"
      >
        <VersionHistoryModal
          headertestid="onhide-history-modal"
          selectedHistory={selectedHistory}
          show={isHistoryOpen}
          onHide={handleCloseVersionHistory}
          versionHistories={versionHistories}
          onHistorySelect={handleSelectHistory}
        />
      </FocusTrapping>
      <FocusTrapping
        onHandleClose={handleClosePolicyLanguage}
        isActive={isLanguageOpen}
        modalId="language-modal-privacy-policy"
      >
        <LanguageModal
          headertestid="onhide-language-modal"
          id="language-modal-privacy-policy"
          key={1}
          show={isLanguageOpen}
          isSiteLanguage={false}
          onHide={handleClosePolicyLanguage}
        />
      </FocusTrapping>
      <div className="privacy-policy__title-section" ref={topRef} tabIndex={-1}>
        <Image image={PrivacyIcon} width={48} height={48} />
        <Typography
          variant="h2"
          className="privacy-policy__title-section__title"
        >
          {t("PRIVACY_POLICY_GPP_PAGE_TITLE")}
          <TTSButton value={t("PRIVACY_POLICY_GPP_PAGE_TITLE")} />
        </Typography>

        <div className="privacy-policy__title-section__buttons">
          {hasVersionHistory && (
            <LinkButton
              id="version-history-button"
              testId="toggle-version-history-modal"
              to="#"
              textClassName={
                "privacy-policy__title-section__buttons__version-history"
              }
              title={t("PRIVACY_POLICY_GPP_BTN_VERSION_HISTORY")}
              icon={SVG_ICONS.PRIVACY_POLICY_HISTORY}
              iconPosition="left"
              iconSize={24}
              underlined={false}
              onClick={() => setHistoryOpen(true)}
              svgIconToolTip={t(
                "PRIVACY_POLICY_GPP_VERSION_HISTORY_POPUP_TITLE"
              )}
            />
          )}

          <LinkButton
            id="policy-language-button"
            to="#"
            testId="pp-gpp-button-change-lang"
            textClassName={"privacy-policy__title-section__buttons__language"}
            title={t("PRIVACY_POLICY_GPP_BTN_CHANGE_LANGUAGE")}
            icon={SVG_ICONS.PRIVACY_POLICY_LANGUAGE}
            iconPosition="left"
            iconSize={24}
            underlined={false}
            onClick={() => setLanguageOpen(true)}
            svgIconToolTip={t("PRIVACY_POLICY_GPP_BTN_CHANGE_LANGUAGE")}
          />
          <a
            data-testid="trigger-download-button"
            download
            href={ppSource}
            className="privacy-policy__title-section__buttons__download"
            onClick={() =>
              GoogleAnalytics.triggerEvent(
                EVENT_CATEGORY_PRIVACY_POLICY,
                EVENT_ACTION_PP_DOWNLOAD_PRIVACY_POLICY
              )
            }
          >
            <Svg
              icon={SVG_ICONS.PRIVACY_POLICY_DOWNLOAD}
              width={24}
              height={24}
              className="privacy-policy__title-section__buttons__download__icon"
              title={t("PRIVACY_POLICY_GPP_BTN_DOWNLOAD")}
            />
            <Typography variant="body2" weight="w700">
              {t("PRIVACY_POLICY_GPP_BTN_DOWNLOAD")}
            </Typography>
          </a>
        </div>
      </div>

      <Container>
        {isIframeLoaded && (
          <iframe
            id="privacy-id"
            src={ppSource}
            width="100%"
            title="privacy-policy"
            onLoad={iframeLoaded}
          />
        )}
      </Container>
    </div>
  );
};
